const INTRO = `Arrange the blocks in the blocks grid the user is
looking at. This is the ONLY way to move blocks: calling this tool performs the
arrangement directly on the grid. You MUST call this tool — and must NOT answer in
prose — for ANY request to arrange, place, order, sort, position, line up, stack,
or otherwise lay out the blocks the user is viewing. Examples that REQUIRE this
tool: "put the applications on top and the business capabilities on the left",
"stack the databases in a column on the right", "sort them alphabetically",
"tidy this up into a grid".

Describe the layout as regions, the way you would sketch a diagram — never as
individual positions. For each group you want to arrange (usually just the top
level, containerId null), give:`;

const REGION_FIELDS = `- 'areas': a CSS grid-template-areas template, one string per row. Areas side by
  side share ONE string (["left middle right"]); separate strings stack top to
  bottom (["top", "bottom"]). Repeat a name to span columns or rows; each area
  must be a rectangle; '.' is empty space.
- 'regions': one per area name, saying which of that group's children go there —
  by 'componentTypes' (type names from the context), exact 'labels' (only those
  nodes, not their children) and/or 'descendantsOf' (each named node plus
  everything beneath it in the hierarchy) — how they flow ('row', 'column',
  'grid' or 'tree'), 'align' and 'sortBy'. Set 'remaining' on one region to catch
  every child no other region claims.
- 'sortBy' "flow" orders a region's blocks along the references between them,
  sources first. Use it whenever the user wants blocks "in sequence", "in order",
  or "matching the flow from source to target", e.g. value stream stages linked
  by "Flows To".
- For a 'grid', 'columns' (or 'rows') fixes how many columns (or rows) it has;
  leave both null to let the area's shape decide. A 'row' or 'column' of more
  than 12 blocks wraps on its own into a landscape block; 'columns' on a 'row'
  (or 'rows' on a 'column') sets where it wraps instead.`;

const STRETCH_FIELD = `- 'stretch' makes each node in a 'column' flow as wide as its area (or, in a
  'row' flow, as tall), so stacked groups line up edge to edge. Without it each
  node is only as wide as its own content. Whenever the user says blocks
  stretch, fill, span or reach across (e.g. "groups which stretch from the left
  group to the right group"), that is a request: set stretch true, even when it
  is phrased as a description. Stretch applies to a single line only, not to a
  row or column that wraps.`;

const CHILD_LAYOUT_FIELD = `- 'childLayout' lays out the children INSIDE each group the region holds — its
  flow, columns or rows, align and sortBy — without writing an arrangement per
  group. Use it whenever the request is about the blocks within groups.`;

const ALIGN_WITH_FIELD = `- 'alignWith' lines each block of the region up with the block in another
  region it is connected to by a reference: {region: that region's name,
  referenceTypes: names from the context's childReferenceTypes, or [] for any}.
  Where you put the two areas sets the direction: this area below the other puts
  each block under its related block; above, over it; to the right or left,
  beside it in its row. Below or above, this area must span every column of the
  other; beside it, every row.`;

const ALIGN_WITH_RULES = `  A layout whose alignWith would line nothing up is
  rejected with the reason; then resend it without that alignWith and tell the
  user why. A region that is the target of an alignWith may itself be
  aligned, for chains such
  as stages, then capabilities, then applications. Use it whenever the user
  wants blocks under, over, beside or lined up with the blocks they relate to,
  e.g. "capabilities directly below each stage", "each application next to the
  capability it supports". It is the only way to place a block by its
  references; without it a region ignores references entirely.`;

const ALIGN_WITHIN_GROUPS = `  References reach into groups: a block related to something nested inside a
  group lines up with that group, not with the nested block itself, so say "under
  the group containing the capability it realizes", not "under its capability".`;

const ALIGN_ACROSS_GROUPS = `  References reach into groups. When the region holds groups and the region it
  aligns with holds a single group, as with two groups of a view grouped by
  type, the blocks inside each group line up with the blocks they relate to
  inside that group: say "each application under the capability it realizes".
  Lay the target group's own blocks out in a row (or a column, for blocks side
  by side), in an arrangement for that group, so there are tracks to line up
  with. Otherwise a block related to something nested inside a group lines up
  with that group, not with the nested block itself, so say "under the group
  containing the capability it realizes".`;

const ALWAYS_SEND = `- Always send every region field, including columns, rows, stretch, childLayout
  and alignWith (null or false when unused).`;

const LAYOUT_SHAPE = `"Large" and "small" groups mean the ones with the most and fewest children
(childCount in the context). "Left", "right", "middle" and the like refer to
where groups sit now: appliedArrangements lists the groups each region holds.

When the view is grouped by component type, each type's blocks sit inside a
group named after the type. A request about blocks of a type ("capabilities in
a horizontal line", "sort the applications") is about the blocks inside that
group: give that group its own arrangement, with its id as containerId, and
leave the top level alone. Move the type groups themselves only when the
request is about where a type sits among the others ("applications at the top").

The template only sets where regions sit relative to each other; the grid sizes
each region to fit its blocks, so keep templates small (2–3 rows and columns).
Repeating an area name never makes a region bigger or wider — only how the
blocks flow changes the shape.

Keep layouts readable: the whole diagram is scaled to fit the screen, so a tall,
narrow layout shrinks every label. Aim for landscape proportions, about 1.3 to 2
times as wide as tall (layoutSize.aspectRatio in the context, for the current
layout). To make a layout wider and less tall:
- put stacked groups side by side: a 'grid' flow with 'columns' instead of a
  'column' flow — more than about six groups stacked in one column is tall;
- give tall groups more childLayout columns;
- place areas side by side (["a b c"]) rather than stacked (["a", "b", "c"]).
Do the reverse to make it narrower. The user's instructions come first:
proportions only decide what the request leaves open, and never replace a
structure the user described (such as side columns with rows between them). When
a request can only be met with a tall layout, do it and say briefly that it will
be tall.`;

const EXAMPLES = `Example — "applications on top, business capabilities on the left":
areas ["top top", "left main"];
regions: top = componentTypes ["Application"], flow "row", align "center";
left = componentTypes ["Business Capability"], flow "column";
main = remaining true, flow "grid".

Example — "a capability map, top-level capabilities across the top with their
sub-capabilities beneath": use the context's hierarchyRoots. areas ["map"];
map = remaining true, flow "tree". Or one area per root, e.g.
areas ["strategy core support"], each region descendantsOf [its root's label],
flow "tree".

Example — "make the value stream stages flow horizontally, with the
capabilities directly below each stage": areas ["stages", "caps"]; stages =
componentTypes ["Value Stream"], flow "row"; caps = componentTypes ["Business
Capability"], flow "column", alignWith {region "stages", referenceTypes
[the type in childReferenceTypes linking them, e.g. "Is Realized By"]}. For
"each application to the right of the capability it supports", put the areas
side by side (["caps apps"]) with the capabilities in a "column" flow.`;

const STRETCH_EXAMPLE = `Example — "narrow groups on the left and right with two columns of children
each, the groups between stretched into rows with one or two rows of children":
areas ["left middle right"]; left and right = labels [the two groups], flow
"column", childLayout {flow "grid", columns 2}; middle = remaining true, flow
"column", stretch true, childLayout {flow "grid", rows 2}.`;

const TYPE_GROUP_EXAMPLE = `Example — "each application under the capability it realizes", grouped by
type: areas ["caps", "apps"]; caps = labels [the capability group], flow
"row"; apps = labels [the application group], flow "row", alignWith {region
"caps", referenceTypes []}; and an arrangement for the capability group with
its blocks in a row.`;

const RESULTS = `Refining a layout ("now center them", "make that three columns"): the context's
appliedArrangements holds the current arrangements in full. Send only the
arrangements you change, each again in full with just the requested change — a
new arrangement for a group replaces the old one entirely, so any region or field
you leave out is lost, while arrangements you do not send are kept. To change the blocks inside the groups a region holds (center them, change
their columns or rows), edit that region's childLayout; do not write an
arrangement per group, except for the blocks of a single type's group in a view
grouped by component type, as above. Example — "center the children inside their groups":
resend the current top-level arrangement with align "center" in each region's
childLayout (adding a childLayout with flow "grid" where there is none).

Children no region claims are placed below the arrangement. Once a layout
applies, the tool returns an 'account' of what the layout now shows, worded from
the layout itself: use it word for word as your summary, and add only what the
request asked for that the regions couldn't do. Before the layout applies, the
context describes the old layout, so don't describe the result from it. Your
assumptions list only choices the user didn't specify, such as which reference
type or order you used; never describe the result in them, since the account
does that, and never state facts about the data or its references there.
A layout you apply stays applied unless a later one sends an arrangement for the
same group, or a childLayout that covers it, which replaces it. When you apply
more than one, the summary gives the account of each layout still in effect, in
the order you applied them.

If the request names component or reference types the view doesn't have (check
childComponentTypes, descendantComponentTypes and childReferenceTypes), use the
closest ones that are there, and after the account say plainly which names were
missing and what you used instead. Never put blocks into a region by their
labels to stand in for a missing type.

Read the request from the user's exact words, and follow them where the
instruction you were given reads more into them. Putting blocks "under",
"below", "beneath", "inside" or "nested in" the blocks they relate to, in either
the user's words or the instruction's, all mean alignWith with this area below
the other. Blocks can't go inside other blocks, so for "inside" or "nested",
align them below and say they're lined up below rather than nested. Don't
decline such a request: alignment is how this view does it. Don't ask which
reference type to use either: unless the user names one, send referenceTypes [].`;

const HAND_EDITS = `This view is Interactive Blocks, where the user also
moves and resizes blocks by hand. A group keeps the layout they left it in, and
blocks added later are placed by the stored arrangement. A new arrangement for a
group lays that group out afresh and discards their hand placement in it, so send
arrangements only for the groups the request is about; groups whose layout comes
from a region's childLayout count as part of that arrangement. layoutSize measures the
layout as it stands, hand placement included.`;

const fieldList = (...fields: string[]): string => fields.join('\n');

/** The layout command as Interactive Blocks describes it, where the user also edits by hand. */
export const INTERACTIVE_BLOCKS_DESCRIPTION = [
  INTRO,
  fieldList(
    REGION_FIELDS,
    CHILD_LAYOUT_FIELD,
    ALIGN_WITH_FIELD,
    ALIGN_ACROSS_GROUPS,
    ALIGN_WITH_RULES,
    ALWAYS_SEND
  ),
  LAYOUT_SHAPE,
  EXAMPLES,
  TYPE_GROUP_EXAMPLE,
  RESULTS,
  HAND_EDITS,
].join('\n\n');

/** The layout command as the experimental Assistant Blocks view describes it. */
export const ASSISTANT_BLOCKS_DESCRIPTION = [
  INTRO,
  fieldList(
    REGION_FIELDS,
    STRETCH_FIELD,
    CHILD_LAYOUT_FIELD,
    ALIGN_WITH_FIELD,
    ALIGN_WITHIN_GROUPS,
    ALIGN_WITH_RULES,
    ALWAYS_SEND
  ),
  LAYOUT_SHAPE,
  EXAMPLES,
  STRETCH_EXAMPLE,
  RESULTS,
].join('\n\n');

export const BLOCKS_CONTEXT_DESCRIPTION = `The context provides 'containers' — the
top level (id null) and the groups inside it at any depth, shallowest first (up to
40; 'omittedContainerCount' says how many deeper ones were left out), each with its
id, label, parentId,
childCount, 'childComponentTypes' (the component type names among its direct
children, with counts), 'descendantComponentTypes' (the types anywhere inside it,
at any depth), 'childReferenceTypes' (the reference types connecting
those children, or anything inside them, to each other, with counts) and 'childLabels' (up to 30 of its children, each with its label and component type,
groups first) with 'omittedLabelCount' (how many were left out), and 'hierarchyRoots' — the top
components of the parent/child hierarchy among its children, with their
descendant counts and their children's labels. Match regions against these
component type names and labels. 'layoutSize' is the current layout's size in blocks across and down, with its
aspect ratio. 'appliedArrangements' holds the current arrangements
in full (areas and regions) and what each did: how many nodes each region holds, how many only arrived through
the 'remaining' region, how many no region placed, and for each region,
'unrelated': how many of its blocks had no related block to line up with, or
null when the region isn't aligned, including when its alignWith could not be
applied — check it before
describing or refining a layout; each region lists the groups it holds. An arrangement only moves the direct children of
its container: to lay out what is inside the groups a region holds, use the
region's childLayout; give a group its own arrangement, with its id as
containerId, only to split its children into regions, or to lay out the blocks
of a single type's group when the view is grouped by component type.`;
